/**
 * Document Types and Categories for Private Lending
 *
 * Issue #497: Universal Document Attachment System
 *
 * Standard document classifications for loan origination workflow.
 * Used for document type tagging, checklist requirements, and organization.
 */

// ============================================================================
// DOCUMENT SCOPE
// ============================================================================

/**
 * Document scope determines reusability:
 * - loan: specific to one loan, not reusable (promissory note, closing disclosure)
 * - personal: belongs to a consumer, reusable across loans (tax return, ID)
 * - entity: belongs to a borrowing entity, reusable across loans (operating agreement)
 * - business: tenant-owned, applies to the entire business (warehouse-line credit box,
 *   rate sheets, fund-provider artifacts) — not per-loan, per-consumer, or per-entity
 */
export enum DocumentScope {
  LOAN = 'loan',
  PERSONAL = 'personal',
  ENTITY = 'entity',
  BUSINESS = 'business',
}

// ============================================================================
// DOCUMENT TYPES
// ============================================================================

/**
 * Financial document types
 */
export const FINANCIAL_DOCUMENT_TYPES = {
  TAX_RETURN_PERSONAL: 'tax_return_personal',
  TAX_RETURN_BUSINESS: 'tax_return_business',
  BANK_STATEMENT_PERSONAL: 'bank_statement_personal',
  BANK_STATEMENT_BUSINESS: 'bank_statement_business',
  PROFIT_LOSS_STATEMENT: 'profit_loss_statement',
  BALANCE_SHEET: 'balance_sheet',
  FINANCIAL_STATEMENT: 'financial_statement',
  INCOME_VERIFICATION: 'income_verification',
  ASSET_STATEMENT: 'asset_statement',
} as const;

/**
 * Property-related document types
 */
export const PROPERTY_DOCUMENT_TYPES = {
  PROPERTY_APPRAISAL: 'property_appraisal',
  TITLE_REPORT: 'title_report',
  PROPERTY_INSURANCE: 'property_insurance',
  PROPERTY_PHOTOS: 'property_photos',
  SCOPE_OF_WORK: 'scope_of_work',
  CONSTRUCTION_BUDGET: 'construction_budget',
  PURCHASE_AGREEMENT: 'purchase_agreement',
  DEED: 'deed',
  SURVEY: 'survey',
  ENVIRONMENTAL_REPORT: 'environmental_report',
  INSPECTION_REPORT: 'inspection_report',
} as const;

/**
 * Identity verification document types
 */
export const IDENTITY_DOCUMENT_TYPES = {
  DRIVERS_LICENSE: 'drivers_license',
  PASSPORT: 'passport',
  SSN_CARD: 'ssn_card',
  UTILITY_BILL: 'utility_bill',
  STATE_ID: 'state_id',
} as const;

/**
 * Legal/Entity document types
 */
export const LEGAL_DOCUMENT_TYPES = {
  ARTICLES_OF_INCORPORATION: 'articles_of_incorporation',
  OPERATING_AGREEMENT: 'operating_agreement',
  GOOD_STANDING_CERTIFICATE: 'good_standing_certificate',
  BYLAWS: 'bylaws',
  PARTNERSHIP_AGREEMENT: 'partnership_agreement',
  TRUST_AGREEMENT: 'trust_agreement',
  EIN_LETTER: 'ein_letter',
} as const;

/**
 * Loan-specific document types
 */
export const LOAN_DOCUMENT_TYPES = {
  SIGNED_LOAN_APPLICATION: 'signed_loan_application',
  PROMISSORY_NOTE: 'promissory_note',
  DEED_OF_TRUST: 'deed_of_trust',
  CLOSING_STATEMENT: 'closing_statement',
  LOAN_ESTIMATE: 'loan_estimate',
  CLOSING_DISCLOSURE: 'closing_disclosure',
  MORTGAGE_NOTE: 'mortgage_note',
  GUARANTY_AGREEMENT: 'guaranty_agreement',
} as const;

/**
 * Authorization and consent document types
 */
export const AUTHORIZATION_DOCUMENT_TYPES = {
  AUTHORIZATION_FORM: 'authorization_form',
  CREDIT_AUTHORIZATION: 'credit_authorization',
  BACKGROUND_CHECK_CONSENT: 'background_check_consent',
  THIRD_PARTY_AUTHORIZATION: 'third_party_authorization',
} as const;

/**
 * Miscellaneous document types
 */
export const OTHER_DOCUMENT_TYPES = {
  PROOF_OF_FUNDS: 'proof_of_funds',
  CONTRACTOR_LICENSE: 'contractor_license',
  EXPERIENCE_DOCUMENTATION: 'experience_documentation',
  LETTER_OF_EXPLANATION: 'letter_of_explanation',
  CONSTRUCTION_PLANS: 'construction_plans',
  BUILDING_PERMITS: 'building_permits',
  RENT_ROLL: 'rent_roll',
  LEASE_AGREEMENTS: 'lease_agreements',
  OTHER: 'other',
} as const;

/**
 * Capital-source document types (Issue #1631 M3).
 * Documents that fund providers (banks, hedge funds, family offices) ship
 * to lender admins. Extracted via AI to populate WarehouseLineLoanTypeProfile
 * + CreditBoxRule + RequiredDoc + RateTier rows.
 */
export const CAPITAL_SOURCE_DOCUMENT_TYPES = {
  WAREHOUSE_LINE_CREDIT_BOX: 'warehouse_line_credit_box',
  WAREHOUSE_LINE_RATE_SHEET: 'warehouse_line_rate_sheet',
} as const;

/**
 * All document types combined
 */
export const DOCUMENT_TYPES = {
  ...FINANCIAL_DOCUMENT_TYPES,
  ...PROPERTY_DOCUMENT_TYPES,
  ...IDENTITY_DOCUMENT_TYPES,
  ...LEGAL_DOCUMENT_TYPES,
  ...LOAN_DOCUMENT_TYPES,
  ...AUTHORIZATION_DOCUMENT_TYPES,
  ...OTHER_DOCUMENT_TYPES,
  ...CAPITAL_SOURCE_DOCUMENT_TYPES,
} as const;

export type DocumentType = (typeof DOCUMENT_TYPES)[keyof typeof DOCUMENT_TYPES];

// ============================================================================
// DOCUMENT CATEGORIES
// ============================================================================

/**
 * High-level document categories for organization
 */
export const DOCUMENT_CATEGORIES = {
  FINANCIAL: 'financial',
  PROPERTY: 'property',
  IDENTITY: 'identity',
  LEGAL: 'legal',
  LOAN: 'loan',
  AUTHORIZATION: 'authorization',
  CAPITAL_SOURCE: 'capital_source',
  OTHER: 'other',
} as const;

export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[keyof typeof DOCUMENT_CATEGORIES];

/**
 * Map document types to their categories
 */
export const DOCUMENT_TYPE_CATEGORY_MAP: Record<DocumentType, DocumentCategory> = {
  // Financial
  tax_return_personal: DOCUMENT_CATEGORIES.FINANCIAL,
  tax_return_business: DOCUMENT_CATEGORIES.FINANCIAL,
  bank_statement_personal: DOCUMENT_CATEGORIES.FINANCIAL,
  bank_statement_business: DOCUMENT_CATEGORIES.FINANCIAL,
  profit_loss_statement: DOCUMENT_CATEGORIES.FINANCIAL,
  balance_sheet: DOCUMENT_CATEGORIES.FINANCIAL,
  financial_statement: DOCUMENT_CATEGORIES.FINANCIAL,
  income_verification: DOCUMENT_CATEGORIES.FINANCIAL,
  asset_statement: DOCUMENT_CATEGORIES.FINANCIAL,

  // Property
  property_appraisal: DOCUMENT_CATEGORIES.PROPERTY,
  title_report: DOCUMENT_CATEGORIES.PROPERTY,
  property_insurance: DOCUMENT_CATEGORIES.PROPERTY,
  property_photos: DOCUMENT_CATEGORIES.PROPERTY,
  scope_of_work: DOCUMENT_CATEGORIES.PROPERTY,
  construction_budget: DOCUMENT_CATEGORIES.PROPERTY,
  purchase_agreement: DOCUMENT_CATEGORIES.PROPERTY,
  deed: DOCUMENT_CATEGORIES.PROPERTY,
  survey: DOCUMENT_CATEGORIES.PROPERTY,
  environmental_report: DOCUMENT_CATEGORIES.PROPERTY,
  inspection_report: DOCUMENT_CATEGORIES.PROPERTY,

  // Identity
  drivers_license: DOCUMENT_CATEGORIES.IDENTITY,
  passport: DOCUMENT_CATEGORIES.IDENTITY,
  ssn_card: DOCUMENT_CATEGORIES.IDENTITY,
  utility_bill: DOCUMENT_CATEGORIES.IDENTITY,
  state_id: DOCUMENT_CATEGORIES.IDENTITY,

  // Legal
  articles_of_incorporation: DOCUMENT_CATEGORIES.LEGAL,
  operating_agreement: DOCUMENT_CATEGORIES.LEGAL,
  good_standing_certificate: DOCUMENT_CATEGORIES.LEGAL,
  bylaws: DOCUMENT_CATEGORIES.LEGAL,
  partnership_agreement: DOCUMENT_CATEGORIES.LEGAL,
  trust_agreement: DOCUMENT_CATEGORIES.LEGAL,
  ein_letter: DOCUMENT_CATEGORIES.LEGAL,

  // Loan
  signed_loan_application: DOCUMENT_CATEGORIES.LOAN,
  promissory_note: DOCUMENT_CATEGORIES.LOAN,
  deed_of_trust: DOCUMENT_CATEGORIES.LOAN,
  closing_statement: DOCUMENT_CATEGORIES.LOAN,
  loan_estimate: DOCUMENT_CATEGORIES.LOAN,
  closing_disclosure: DOCUMENT_CATEGORIES.LOAN,
  mortgage_note: DOCUMENT_CATEGORIES.LOAN,
  guaranty_agreement: DOCUMENT_CATEGORIES.LOAN,

  // Authorization
  authorization_form: DOCUMENT_CATEGORIES.AUTHORIZATION,
  credit_authorization: DOCUMENT_CATEGORIES.AUTHORIZATION,
  background_check_consent: DOCUMENT_CATEGORIES.AUTHORIZATION,
  third_party_authorization: DOCUMENT_CATEGORIES.AUTHORIZATION,

  // Other
  proof_of_funds: DOCUMENT_CATEGORIES.OTHER,
  contractor_license: DOCUMENT_CATEGORIES.OTHER,
  experience_documentation: DOCUMENT_CATEGORIES.OTHER,
  letter_of_explanation: DOCUMENT_CATEGORIES.OTHER,
  construction_plans: DOCUMENT_CATEGORIES.OTHER,
  building_permits: DOCUMENT_CATEGORIES.OTHER,
  rent_roll: DOCUMENT_CATEGORIES.OTHER,
  lease_agreements: DOCUMENT_CATEGORIES.OTHER,
  other: DOCUMENT_CATEGORIES.OTHER,

  // Capital Source (Issue #1631 M3)
  warehouse_line_credit_box: DOCUMENT_CATEGORIES.CAPITAL_SOURCE,
  warehouse_line_rate_sheet: DOCUMENT_CATEGORIES.CAPITAL_SOURCE,
};

/**
 * Map document types to their default scope (reusability level)
 */
export const DOCUMENT_SCOPE_MAP: Record<DocumentType, DocumentScope> = {
  // Financial — personal documents
  tax_return_personal: DocumentScope.PERSONAL,
  bank_statement_personal: DocumentScope.PERSONAL,
  income_verification: DocumentScope.PERSONAL,

  // Financial — entity documents
  tax_return_business: DocumentScope.ENTITY,
  bank_statement_business: DocumentScope.ENTITY,
  profit_loss_statement: DocumentScope.ENTITY,
  balance_sheet: DocumentScope.ENTITY,
  financial_statement: DocumentScope.ENTITY,
  asset_statement: DocumentScope.ENTITY,

  // Property — all loan-scoped
  property_appraisal: DocumentScope.LOAN,
  title_report: DocumentScope.LOAN,
  property_insurance: DocumentScope.LOAN,
  property_photos: DocumentScope.LOAN,
  scope_of_work: DocumentScope.LOAN,
  construction_budget: DocumentScope.LOAN,
  purchase_agreement: DocumentScope.LOAN,
  deed: DocumentScope.LOAN,
  survey: DocumentScope.LOAN,
  environmental_report: DocumentScope.LOAN,
  inspection_report: DocumentScope.LOAN,

  // Identity — all personal-scoped
  drivers_license: DocumentScope.PERSONAL,
  passport: DocumentScope.PERSONAL,
  ssn_card: DocumentScope.PERSONAL,
  utility_bill: DocumentScope.PERSONAL,
  state_id: DocumentScope.PERSONAL,

  // Legal — all entity-scoped
  articles_of_incorporation: DocumentScope.ENTITY,
  operating_agreement: DocumentScope.ENTITY,
  good_standing_certificate: DocumentScope.ENTITY,
  bylaws: DocumentScope.ENTITY,
  partnership_agreement: DocumentScope.ENTITY,
  trust_agreement: DocumentScope.ENTITY,
  ein_letter: DocumentScope.ENTITY,

  // Loan — all loan-scoped
  signed_loan_application: DocumentScope.LOAN,
  promissory_note: DocumentScope.LOAN,
  deed_of_trust: DocumentScope.LOAN,
  closing_statement: DocumentScope.LOAN,
  loan_estimate: DocumentScope.LOAN,
  closing_disclosure: DocumentScope.LOAN,
  mortgage_note: DocumentScope.LOAN,
  guaranty_agreement: DocumentScope.LOAN,

  // Authorization — mixed scopes
  authorization_form: DocumentScope.LOAN,
  credit_authorization: DocumentScope.PERSONAL,
  background_check_consent: DocumentScope.PERSONAL,
  third_party_authorization: DocumentScope.LOAN,

  // Other — all loan-scoped
  proof_of_funds: DocumentScope.LOAN,
  contractor_license: DocumentScope.LOAN,
  experience_documentation: DocumentScope.LOAN,
  letter_of_explanation: DocumentScope.LOAN,
  construction_plans: DocumentScope.LOAN,
  building_permits: DocumentScope.LOAN,
  rent_roll: DocumentScope.LOAN,
  lease_agreements: DocumentScope.LOAN,
  other: DocumentScope.LOAN,

  // Capital Source — all business-scoped (tenant-owned, applies to entire business)
  warehouse_line_credit_box: DocumentScope.BUSINESS,
  warehouse_line_rate_sheet: DocumentScope.BUSINESS,
};

/**
 * Human-readable display names for document types
 */
export const DOCUMENT_TYPE_DISPLAY_NAMES: Record<DocumentType, string> = {
  // Financial
  tax_return_personal: 'Personal Tax Return',
  tax_return_business: 'Business Tax Return',
  bank_statement_personal: 'Personal Bank Statement',
  bank_statement_business: 'Business Bank Statement',
  profit_loss_statement: 'Profit & Loss Statement',
  balance_sheet: 'Balance Sheet',
  financial_statement: 'Financial Statement',
  income_verification: 'Income Verification',
  asset_statement: 'Asset Statement',

  // Property
  property_appraisal: 'Property Appraisal',
  title_report: 'Title Report',
  property_insurance: 'Property Insurance',
  property_photos: 'Property Photos',
  scope_of_work: 'Scope of Work',
  construction_budget: 'Construction Budget',
  purchase_agreement: 'Purchase Agreement',
  deed: 'Deed',
  survey: 'Survey',
  environmental_report: 'Environmental Report',
  inspection_report: 'Inspection Report',

  // Identity
  drivers_license: "Driver's License",
  passport: 'Passport',
  ssn_card: 'Social Security Card',
  utility_bill: 'Utility Bill',
  state_id: 'State ID',

  // Legal
  articles_of_incorporation: 'Articles of Incorporation',
  operating_agreement: 'Operating Agreement',
  good_standing_certificate: 'Good Standing Certificate',
  bylaws: 'Bylaws',
  partnership_agreement: 'Partnership Agreement',
  trust_agreement: 'Trust Agreement',
  ein_letter: 'EIN Letter',

  // Loan
  signed_loan_application: 'Signed Loan Application',
  promissory_note: 'Promissory Note',
  deed_of_trust: 'Deed of Trust',
  closing_statement: 'Closing Statement',
  loan_estimate: 'Loan Estimate',
  closing_disclosure: 'Closing Disclosure',
  mortgage_note: 'Mortgage Note',
  guaranty_agreement: 'Guaranty Agreement',

  // Authorization
  authorization_form: 'Authorization Form',
  credit_authorization: 'Credit Authorization',
  background_check_consent: 'Background Check Consent',
  third_party_authorization: 'Third Party Authorization',

  // Other
  proof_of_funds: 'Proof of Funds',
  contractor_license: 'Contractor License',
  experience_documentation: 'Experience Documentation',
  letter_of_explanation: 'Letter of Explanation',
  construction_plans: 'Construction Plans',
  building_permits: 'Building Permits',
  rent_roll: 'Rent Roll',
  lease_agreements: 'Lease Agreements',
  other: 'Other',

  // Capital Source
  warehouse_line_credit_box: 'Warehouse Line Credit Box',
  warehouse_line_rate_sheet: 'Warehouse Line Rate Sheet',
};

// ============================================================================
// LOAN TYPES (for checklist association)
// ============================================================================

/**
 * Private lending loan types.
 *
 * @deprecated Issue #1388 — Per-business `LoanProduct` is now the runtime
 * source of truth for what a lender offers. This constant is kept only as a
 * **seed source** for `LoanProduct.category` and as the legacy enum referenced
 * by document-pipeline checklists. Do NOT use as a runtime enum in new code —
 * read the per-business `loanProducts.list` tRPC query instead.
 */
export const LOAN_TYPES = {
  BRIDGE: 'bridge',
  FIX_AND_FLIP: 'fix_and_flip',
  DSCR: 'dscr',
  RENTAL: 'rental',
  GROUND_UP: 'ground_up',
  CONSTRUCTION: 'construction',
  COMMERCIAL: 'commercial',
  MULTIFAMILY: 'multifamily',
  MULTI_FAMILY: 'multi_family',
  LAND: 'land',
  MIXED_USE: 'mixed_use',
} as const;

export type PrivateLendingLoanType = (typeof LOAN_TYPES)[keyof typeof LOAN_TYPES];

/**
 * Human-readable display names for loan types
 */
export const LOAN_TYPE_DISPLAY_NAMES: Record<string, string> = {
  bridge: 'Bridge Loan',
  fix_and_flip: 'Fix & Flip',
  dscr: 'DSCR Rental',
  rental: 'Rental / DSCR',
  ground_up: 'Ground-Up Construction',
  construction: 'Construction',
  commercial: 'Commercial',
  multifamily: 'Multifamily',
  multi_family: 'Multi-Family',
  land: 'Land',
  mixed_use: 'Mixed Use',
};

/** Maps variant loan type names to their canonical form */
export const LOAN_TYPE_ALIASES: Record<string, string> = {
  rental: 'dscr',
  construction: 'ground_up',
  multi_family: 'multifamily',
};

/** Resolves a loan type string to its canonical form for template lookup */
export function canonicalizeLoanType(loanType: string): string {
  return LOAN_TYPE_ALIASES[loanType] ?? loanType;
}

// ============================================================================
// ENTITY ATTACHMENT TYPES
// ============================================================================

/**
 * Entity types that support document attachments
 */
export const ENTITY_ATTACHMENT_TYPES = {
  CASE: 'CASE',
  CONSUMER: 'CONSUMER',
  LEAD: 'LEAD',
  PARTNER: 'PARTNER',
  COMMENT: 'COMMENT',
  TASK: 'TASK',
  INTAKE_SUBMISSION: 'INTAKE_SUBMISSION',
  CUSTOM_ENTITY: 'CUSTOM_ENTITY',
  BORROWING_ENTITY: 'BORROWING_ENTITY',
} as const;

export type AttachmentEntityType = (typeof ENTITY_ATTACHMENT_TYPES)[keyof typeof ENTITY_ATTACHMENT_TYPES];

// ============================================================================
// VERIFICATION STATUS
// ============================================================================

/**
 * Document verification workflow status
 */
export const DOCUMENT_VERIFICATION_STATUS = {
  PENDING: 'PENDING',
  VERIFIED: 'VERIFIED',
  REJECTED: 'REJECTED',
  EXPIRED: 'EXPIRED',
  NEEDS_REUPLOAD: 'NEEDS_REUPLOAD',
} as const;

export type DocumentVerificationStatus =
  (typeof DOCUMENT_VERIFICATION_STATUS)[keyof typeof DOCUMENT_VERIFICATION_STATUS];

/**
 * Human-readable display names for verification status
 */
export const VERIFICATION_STATUS_DISPLAY_NAMES: Record<DocumentVerificationStatus, string> = {
  PENDING: 'Pending Review',
  VERIFIED: 'Verified',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
  NEEDS_REUPLOAD: 'Needs Re-upload',
};

/**
 * Color codes for verification status (for UI)
 */
export const VERIFICATION_STATUS_COLORS: Record<DocumentVerificationStatus, string> = {
  PENDING: 'warning',
  VERIFIED: 'success',
  REJECTED: 'error',
  EXPIRED: 'error',
  NEEDS_REUPLOAD: 'warning',
};

/**
 * Valid verification status transitions.
 * Used to enforce state machine rules on status changes.
 */
export const VALID_VERIFICATION_TRANSITIONS: Record<DocumentVerificationStatus, DocumentVerificationStatus[]> = {
  PENDING: ['VERIFIED', 'REJECTED', 'NEEDS_REUPLOAD', 'EXPIRED'],
  VERIFIED: ['REJECTED', 'NEEDS_REUPLOAD', 'EXPIRED'],
  REJECTED: ['PENDING', 'VERIFIED', 'NEEDS_REUPLOAD'],
  EXPIRED: ['PENDING', 'NEEDS_REUPLOAD'],
  NEEDS_REUPLOAD: ['PENDING', 'VERIFIED', 'REJECTED', 'EXPIRED'],
};
